/**
 * One task in full (`/tasks?task=<id>`): who, why, More context, the draft
 * and the actions – the shared `TaskCard` –, then where the task belongs
 * (contact, campaign, bundle, who proposed it, who executes it) and its
 * history. A closed task can be reopened here; an open employee proposal can
 * be sent back with a note (Request changes…), and after the seat revised it
 * the drawer shows what changed. An admin also sees the queue jobs that carry
 * the task's id, linked into the employee drawer.
 * The page owns the URL; the drawer owns its detail query.
 */
import { useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { tasksApi, useTaskDetail, type TaskDetail, type TaskEvent, type TaskRevision } from "../../api/tasks";
import { agentDrawerUrl } from "../../lib/aiTeam";
import { berlinDateTime } from "../../lib/console";
import { useTaskActions } from "../../lib/hooks/useTaskActions";
import { openPrimary } from "../../lib/taskActions";
import { groupCampaignHref, isBeingRevised, taskTypeLabel } from "../../lib/taskGroups";
import { Drawer } from "../admin/Drawer";
import { Pill } from "../Pill";
import { ErrorStrip } from "../QueryBoundary";
import { useToast } from "../toast/context";
import { RequestChangesModal, type RequestChangesTarget } from "./RequestChangesModal";
import { TaskCard } from "./TaskCard";

const FIELD_LABEL: Record<string, string> = {
  title: "Title",
  description: "Why",
  details: "More context",
  message_draft: "Draft",
  expires_at: "Expires",
  batch: "Group",
  bundle_id: "Bundle",
  campaign_slug: "Campaign",
};

/** What the seat's last revision changed – the owner's note, the seat's
 * note and the values before it. */
function LastRevision({ revision }: { revision: TaskRevision }) {
  const fields = Object.entries(revision.fields ?? {});
  return (
    <section>
      <h3 className="label">Changed after your note</h3>
      <div className="text-sm space-y-1.5">
        {revision.owner_note ? (
          <div className="text-ink-medium whitespace-pre-wrap">Your note: {revision.owner_note}</div>
        ) : null}
        <div>
          <span className="font-mono">{revision.by ?? "the seat"}</span> · version {revision.version} → {revision.version + 1}
          {revision.seat_note ? <span className="text-ink-medium"> – {revision.seat_note}</span> : null}
        </div>
        {fields.length === 0 ? <div className="text-ink-low">Nothing changed.</div> : null}
        {fields.map(([name, before]) => (
          <details key={name}>
            <summary className="cursor-pointer text-ink-low">{FIELD_LABEL[name] ?? name} before</summary>
            <pre className="input font-sans text-sm whitespace-pre-wrap mt-1">
              {before === null || before === undefined ? "–" : typeof before === "string" ? before : JSON.stringify(before)}
            </pre>
          </details>
        ))}
      </div>
    </section>
  );
}

const CLOSED = ["completed", "dismissed"];

function Meta({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex gap-3">
      <dt className="w-28 shrink-0 text-ink-low">{label}</dt>
      <dd className="min-w-0 text-ink-medium">{children}</dd>
    </div>
  );
}

function HistoryRow({ event }: { event: TaskEvent }) {
  return (
    <li className="flex gap-3 py-1.5">
      <span className="w-36 shrink-0 font-mono text-xs text-ink-low pt-0.5">
        {event.at ? berlinDateTime(event.at) : "—"}
      </span>
      <span className="min-w-0">
        <span className="text-ink">{event.text}</span>
        {event.actor && event.kind !== "created" ? (
          <span className="text-ink-low"> · {event.actor}</span>
        ) : null}
        {event.note ? <div className="text-ink-medium whitespace-pre-wrap">{event.note}</div> : null}
      </span>
    </li>
  );
}

function TaskMeta({ detail }: { detail: TaskDetail }) {
  const task = detail.item;
  const campaignHref = groupCampaignHref({
    bundleId: detail.bundle?.id ?? null,
    campaignSlug: detail.campaign?.slug ?? null,
  });
  return (
    <dl className="space-y-1.5 text-sm">
      {detail.contact ? (
        <Meta label="Contact">
          <Link to={`/contacts/${detail.contact.id}`} className="link">
            {detail.contact.name || detail.contact.id}
          </Link>
          {detail.contact.company ? <span className="text-ink-low"> · {detail.contact.company}</span> : null}
        </Meta>
      ) : null}
      {detail.campaign && campaignHref ? (
        <Meta label="Campaign">
          <Link to={campaignHref} className="link">
            {detail.campaign.name}
            {detail.bundle?.title ? ` · ${detail.bundle.title}` : ""}
          </Link>
        </Meta>
      ) : null}
      <Meta label="Type">{taskTypeLabel(task.task_type)}</Meta>
      <Meta label="From">
        {task.agent_slug ? <span className="font-mono">{task.agent_slug}</span> : "You"}
      </Meta>
      {detail.executor === "agent" ? (
        <Meta label="Done by">
          <span className="font-mono">{detail.assigned_agent_slug ?? "an AI employee"}</span>
        </Meta>
      ) : null}
      {task.expires_at ? (
        <Meta label="Expires">
          <span className="font-mono">{berlinDateTime(task.expires_at)}</span>
        </Meta>
      ) : null}
      {task.linkedin_url || task.action_url ? (
        <Meta label="Link">
          <a href={task.linkedin_url ?? task.action_url ?? undefined} target="_blank" rel="noreferrer" className="link break-all">
            {task.linkedin_url ?? task.action_url} ↗
          </a>
        </Meta>
      ) : null}
      {detail.agent_error ? (
        <Meta label="Agent error">
          <span className="text-danger">{detail.agent_error}</span>
        </Meta>
      ) : null}
    </dl>
  );
}

export function TaskDrawer({ taskId, onClose }: { taskId: string | null; onClose: () => void }) {
  const query = useTaskDetail(taskId);
  const actions = useTaskActions();
  const toast = useToast();
  const qc = useQueryClient();
  const [nowMs] = useState(() => Date.now());
  const [reopening, setReopening] = useState(false);
  const [changesTarget, setChangesTarget] = useState<RequestChangesTarget | null>(null);
  const detail = query.data?.detail;
  const task = detail?.item;
  const title = task ? task.contact_name || task.title || taskTypeLabel(task.task_type) : "Task";
  const closed = task ? CLOSED.includes(task.status) : false;
  const canReopen = closed && detail?.executor === "human" && !detail?.withdrawal;
  const canSendBack = !!task && !closed && !!task.agent_id && detail?.executor === "human" && !isBeingRevised(task);
  const lastRevision = detail?.revisions.length ? detail.revisions[detail.revisions.length - 1] : null;

  async function reopen() {
    if (!task) return;
    setReopening(true);
    try {
      await tasksApi.reopen(task.task_id);
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["tasks"] }),
        qc.invalidateQueries({ queryKey: ["console"] }),
      ]);
      toast.ok("Back in your to-do list");
    } catch (err) {
      toast.error(err);
    } finally {
      setReopening(false);
    }
  }

  return (
    <Drawer open={!!taskId} onClose={onClose} eyebrow="Task" title={title}>
      {query.isLoading ? (
        <div className="text-sm text-ink-low">Loading…</div>
      ) : query.isError && !detail ? (
        <ErrorStrip
          message="The task could not be loaded."
          onRetry={() => void query.refetch()}
          retrying={query.isFetching}
        />
      ) : detail && task ? (
        <div className="space-y-6">
          <TaskCard
            task={task}
            nowMs={nowMs}
            busy={actions.busyId === task.task_id}
            emphasis
            onPrimary={() =>
              void openPrimary(task).then((r) => {
                if (r.error) toast.error(r.error);
              })
            }
            onClose={() => void actions.complete(task)}
            onDecline={(reason) => actions.decline(task, reason)}
          />

          {canSendBack ? (
            <div className="flex items-center gap-3 text-sm">
              <button
                type="button"
                className="btn btn-second btn-sm"
                onClick={() =>
                  setChangesTarget({
                    taskIds: [task.task_id],
                    label: title,
                    count: 1,
                    seats: task.agent_slug ? [task.agent_slug] : [],
                  })
                }
              >
                Request changes…
              </button>
              <span className="text-ink-low">
                version <span className="font-mono">{task.version}</span> – the seat revises it in place
              </span>
            </div>
          ) : null}

          {lastRevision ? <LastRevision revision={lastRevision} /> : null}

          {canReopen ? (
            <div className="flex items-center gap-3 text-sm">
              <Pill value={task.status} />
              <button type="button" className="btn btn-second btn-sm" disabled={reopening} onClick={() => void reopen()}>
                Reopen
              </button>
            </div>
          ) : null}

          <section>
            <h3 className="label">About</h3>
            <TaskMeta detail={detail} />
          </section>

          <section>
            <h3 className="label">History</h3>
            <ol className="text-sm divide-y" style={{ borderColor: "var(--c-rule)" }}>
              {detail.history.map((event, i) => (
                <HistoryRow key={`${event.kind}-${i}`} event={event} />
              ))}
            </ol>
          </section>

          {detail.jobs && detail.jobs.length > 0 ? (
            <section>
              <h3 className="label">Jobs</h3>
              <ul className="text-sm space-y-1">
                {detail.jobs.map((job) => (
                  <li key={job.id} className="flex items-center gap-2">
                    {job.scope_type === "agent" ? (
                      <Link to={agentDrawerUrl(job.scope_id, { tab: "jobs", jobId: job.id })} className="link font-mono">
                        {job.job_type}
                      </Link>
                    ) : (
                      <span className="font-mono">{job.job_type}</span>
                    )}
                    <Pill value={job.status} />
                    <span className="font-mono text-xs text-ink-low">{berlinDateTime(job.created_at)}</span>
                    {job.error ? <span className="text-xs text-danger truncate">{job.error}</span> : null}
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </div>
      ) : (
        <div className="text-sm text-ink-low">This task does not exist.</div>
      )}
      <RequestChangesModal target={changesTarget} onClose={() => setChangesTarget(null)} />
    </Drawer>
  );
}
